/**
 * ทางเดียวที่หน้าจอหน้างานใช้บันทึกคำสั่ง — เข้าคิวในเครื่อง ไม่ส่งขึ้นเซิร์ฟเวอร์
 *
 * D-075: หน้าจอ capture + enqueue เท่านั้น การส่งขึ้น sync API เป็นหน้าที่ของ
 * sync engine ที่เป็น Work Order แยกและยังไม่มีในบิลด์นี้ ไฟล์นี้จึงไม่ import
 * `apiFetch`/`authorizedFetch` และไม่แตะ `fetch` เลย — เทสต์ของหน้าจอทุกตัว
 * พิสูจน์ข้อนี้ด้วยการดักจับ `fetch` แล้วยืนยันว่าไม่เคยถูกเรียก
 *
 * **สำเร็จที่นี่แปลว่า "เก็บไว้ในเครื่องแล้ว" เท่านั้น ไม่ใช่ server รับแล้ว**
 * (`commandEnvelope.ts`) ผู้เรียกจึงต้องขึ้นข้อความว่า "เข้าคิวแล้ว" ห้ามขึ้นว่า
 * บันทึก/โพสต์สำเร็จ
 */

import { serializeShopFloorPayload, type ShopFloorCommand } from './commands';
import { readShopFloorIdentity, type ShopFloorIdentity } from './identity';
import {
  OfflineQueueError,
  type OfflineCommandRecord,
  type OfflineQueueErrorReason,
} from '../offline/commandEnvelope';
import { enqueueOfflineCommand } from '../offline/commandQueue';

export type EnqueueShopFloorCommandResult =
  /** อยู่ในคิวในเครื่องแล้ว สถานะ `PENDING_LOCAL` รอ sync engine มารับ */
  | { readonly status: 'queued'; readonly record: OfflineCommandRecord }
  /** เก็บลงเครื่องไม่ได้ — คำสั่งนี้ **ไม่มีอยู่** ที่ไหนเลย ต้องบอกผู้ใช้ตรง ๆ */
  | { readonly status: 'failed'; readonly reason: OfflineQueueErrorReason };

export interface EnqueueShopFloorCommandOptions {
  /** inject ได้เพื่อการทดสอบ — production อ่านจาก seam เดียวใน `identity.ts` */
  readonly identity?: ShopFloorIdentity;
}

/**
 * บันทึกคำสั่งหนึ่งใบลงคิวออฟไลน์
 *
 * คืนผลลัพธ์แทนการโยน เพราะทุกความล้มเหลวของคิวเป็นเรื่องที่หน้าจอต้องเล่าให้
 * ผู้ปฏิบัติงานฟัง (พื้นที่เต็ม, เบราว์เซอร์ปิด storage, แท็บอื่นค้าง) ไม่ใช่
 * ข้อผิดพลาดที่ปล่อยให้กลายเป็น unhandled rejection แล้วหน้าจอค้างเงียบ ๆ
 *
 * ความล้มเหลวที่ไม่ใช่ `OfflineQueueError` ถูกนับเป็น `WRITE_FAILED` โดยตั้งใจ:
 * อะไรก็ตามที่ทำให้เขียนไม่จบ ผลกับผู้ใช้เหมือนกันทุกประการคือ **คำสั่งไม่ได้ถูก
 * เก็บไว้** และต้องไม่ถูกแสดงว่าเข้าคิวแล้ว
 */
export async function enqueueShopFloorCommand(
  command: ShopFloorCommand,
  options: EnqueueShopFloorCommandOptions = {},
): Promise<EnqueueShopFloorCommandResult> {
  const identity = options.identity ?? readShopFloorIdentity();

  try {
    const record = await enqueueOfflineCommand({
      device_id: identity.device_id,
      employee_id: identity.employee_id,
      command_type: command.command_type,
      payload: serializeShopFloorPayload(command),
    });
    return { status: 'queued', record };
  } catch (error) {
    const reason: OfflineQueueErrorReason =
      error instanceof OfflineQueueError ? error.reason : 'WRITE_FAILED';
    return { status: 'failed', reason };
  }
}
